import { useResizeObserver } from '@react-aria/utils';
import { merge } from 'lodash-es';
import { type RefObject, useMemo, useRef, useState } from 'react';

import type { GutterProp } from '../../utilities/shared/Gutter';
import type { MaxWidthProps } from '../../utilities/shared/MaxWidth';
import type { MinWidthProps } from '../../utilities/shared/MinWidth';
import type { WidthProps } from '../../utilities/shared/Width';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { HopsPreviewPayload } from './atoms/utils';
import { containerScales } from '../../common/containers';
import { sizing } from '../../common/sizing';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import {
  type PreviewCardHandle,
  createPreviewCardHandle,
  PreviewCard,
} from '../../formatting/PreviewCard/PreviewCard';
import { styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import {
  type HopConnectionProps,
  HopCollapseIndicator,
  HopConnection,
  HopConnectionSharedPreviewContent,
} from './atoms/HopsConnection';
import { type HopObjectProps, HopObject, HopObjectSharedPreviewContent } from './atoms/HopsObject';
import { type HopSpanProps, HopSpan } from './atoms/HopsSpan';

export type {
  HopConnectionAnimateProp,
  HopConnectionPreviewPayload,
  HopConnectionTrackVariantProp,
} from './atoms/HopsConnection';
export { HopConnectionSharedPreviewContent } from './atoms/HopsConnection';
export type { HopObjectProps } from './atoms/HopsObject';
export { HopObjectSharedPreviewContent } from './atoms/HopsObject';
export type { HopSpanProps } from './atoms/HopsSpan';
export type { HopObjectPreviewPayload, HopPreview, HopsPreviewPayload } from './atoms/utils';

export type HopProps = HopConnectionProps | HopObjectProps | HopSpanProps;

/** Layout axis for a `Hops` diagram. */
export type HopsDirection = 'column' | 'row';

/**
 * Responsive `direction` config: switches between `narrow` and `notNarrow` based on the
 * Hops container's own measured width. The threshold comes from a named container family
 * in `containers.ts` (see `containerScales`), so callers can opt into a different family
 * (`drawer`, `dialog`, `application`, `stage`) without redefining breakpoints.
 */
export type HopsResponsiveDirection = {
  /** Container family from `containers.ts` whose `narrow` scale is used as the breakpoint. Default: `'pane'`. */
  container?: keyof typeof containerScales;
  /** Direction when measured width is at or below the family's narrow threshold. Default: `'column'`. */
  narrow?: HopsDirection;
  /** Direction when measured width is above the family's narrow threshold. Default: `'row'`. */
  notNarrow?: HopsDirection;
};

export type HopsDirectionProp = HopsDirection | HopsResponsiveDirection;

function isResponsiveDirection(
  direction: HopsDirectionProp | undefined,
): direction is HopsResponsiveDirection {
  return typeof direction === 'object' && direction !== null;
}

/**
 * Resolves the responsive `direction` config into a concrete axis by observing the
 * supplied ref's width. Returns `notNarrow` until the first measurement to avoid an
 * initial column→row flip on wide layouts. When `config` is `null`, the hook stays
 * inert (the caller is using a fixed direction) but still runs in a stable order.
 */
function useResponsiveDirection(
  ref: RefObject<HTMLDivElement | null>,
  config: HopsResponsiveDirection | null,
): HopsDirection {
  const family = config?.container ?? 'pane';
  const narrowDir = config?.narrow ?? 'column';
  const notNarrowDir = config?.notNarrow ?? 'row';
  const threshold = containerScales[family].narrow;

  const [direction, setDirection] = useState<HopsDirection>(notNarrowDir);

  useResizeObserver({
    ref: config ? ref : { current: null },
    onResize: () => {
      const width = ref.current?.getBoundingClientRect().width ?? 0;
      if (width <= 0) return;
      const next: HopsDirection = width <= threshold ? narrowDir : notNarrowDir;
      setDirection((prev) => (prev === next ? prev : next));
    },
  });

  return direction;
}

function HopsSharedPreviewContent({ payload }: { payload: HopsPreviewPayload }) {
  if (payload.kind === 'object') {
    return <HopObjectSharedPreviewContent payload={payload} />;
  }
  return <HopConnectionSharedPreviewContent payload={payload} />;
}

type ObjectOrSpanHop = Extract<HopProps, { type: 'object' | 'span' }>;

/** Stable empty list for collapsed layout (avoids new array identity per memo run). */
const EMPTY_HOP_LIST: HopProps[] = [];

function augmentSpanHopForObjectNeighbors(
  hop: ObjectOrSpanHop,
  hopIndex: number,
  segment: HopProps[],
  prevAcrossBoundary?: HopProps,
): ObjectOrSpanHop;
function augmentSpanHopForObjectNeighbors(
  hop: HopProps,
  hopIndex: number,
  segment: HopProps[],
  prevAcrossBoundary?: HopProps,
): HopProps;
function augmentSpanHopForObjectNeighbors(
  hop: HopProps,
  hopIndex: number,
  segment: HopProps[],
  prevAcrossBoundary?: HopProps,
): HopProps {
  if (hop.type !== 'span') {
    return hop;
  }
  const prev = hopIndex === 0 ? prevAcrossBoundary : segment[hopIndex - 1];
  const next = segment[hopIndex + 1];
  const padStart = prev?.type === 'object';
  const padEnd = next?.type === 'object';
  if (!padStart && !padEnd) {
    return hop;
  }
  let objectAdjacentPadding: 'both' | 'end' | 'start';
  if (padStart && padEnd) {
    objectAdjacentPadding = 'both';
  } else if (padStart) {
    objectAdjacentPadding = 'start';
  } else {
    objectAdjacentPadding = 'end';
  }
  return {
    ...hop,
    bufferEnd: objectAdjacentPadding,
  };
}

function Hop({
  direction,
  hop,
  previewHandle,
  size,
}: {
  direction: 'column' | 'row';
  hop: HopProps;
  previewHandle: PreviewCardHandle<HopsPreviewPayload>;
  size: 'small' | 'large';
}) {
  switch (hop.type) {
    case 'connection':
      return (
        <HopConnection direction={direction} {...hop} size={size} previewHandle={previewHandle} />
      );
    case 'span':
      return <HopSpan direction={direction} {...hop} size={size} />;
    case 'object':
      return <HopObject direction={direction} {...hop} size={size} previewHandle={previewHandle} />;
  }
}

const HopsDisplay = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
      row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
    },
  },
});
HopsDisplay.displayName = 'HopsDisplay';

const HopsSizer = styled('div', {
  display: 'flex',
  overflow: 'hidden',
  pointerEvents: 'none',

  variants: {
    direction: {
      column: {
        display: 'none',
      },
      row: {
        flexDirection: 'row',
        minWidth: 'min-content',
        heightAll: 0,
      },
    },
  },
});
HopsSizer.displayName = 'HopsSizer';

const HopsAccess = styled('div', {
  position: 'absolute',
  top: `$${sizing.secondary}`,
  left: `$${sizing.primary}`,
});
HopsAccess.displayName = 'HopsAccess';

const HopsContainer = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      column: {
        flexDirection: 'row',
        minHeight: 'min-content',
      },
      row: {
        flexDirection: 'column',
        minWidth: 'min-content',
      },
    },
  },
});
HopsContainer.displayName = 'HopsContainer';

export type HopsCollapsible = {
  /**
   * The number of hops to show at the start of the hops.
   */
  start?: number;
  /**
   * The number of hops to show at the end of the hops.
   */
  end?: number;
};

type HopsProps = {
  accessLevel?: AccessLevelProp | null;
  collapsible?: HopsCollapsible;
  /**
   * Layout axis. Accepts a literal `'row' | 'column'` for fully controlled layouts, or a
   * `HopsResponsiveDirection` config that switches direction based on the Hops' own
   * measured width against a named container family in `containers.ts`. When omitted,
   * defaults to a responsive layout against `containers.pane`.
   */
  direction?: HopsDirectionProp;
  hops: HopProps[];
  /** Sizing applied uniformly to every hop. Defaults to `'small'`. */
  size?: 'small' | 'large';
} & WidthProps &
  MaxWidthProps &
  MinWidthProps &
  GutterProp;

const DEFAULT_RESPONSIVE_DIRECTION: HopsResponsiveDirection = { container: 'pane' };

export function Hops({
  accessLevel,
  collapsible,
  direction: directionProp,
  gutter = { context: 'application', edge: 'all' },
  hops,
  size = 'small',
  width,
  maxWidth,
  minWidth,
}: HopsProps) {
  const [expanded, setExpanded] = useState(false);
  const previewHandle = useMemo(() => createPreviewCardHandle<HopsPreviewPayload>(), []);

  const containerRef = useRef<HTMLDivElement>(null);
  let responsiveConfig: HopsResponsiveDirection | null = null;
  if (directionProp === undefined) {
    responsiveConfig = DEFAULT_RESPONSIVE_DIRECTION;
  } else if (isResponsiveDirection(directionProp)) {
    responsiveConfig = directionProp;
  }
  const measuredDirection = useResponsiveDirection(containerRef, responsiveConfig);
  const direction: HopsDirection = responsiveConfig
    ? measuredDirection
    : (directionProp as HopsDirection);

  const { startHops, endHops, collapsedCount } = useMemo(() => {
    if (!collapsible || expanded) {
      return { startHops: hops, endHops: EMPTY_HOP_LIST, collapsedCount: 0 };
    }

    const startCount = collapsible.start ?? 5;
    const endCount = collapsible.end ?? 5;

    const objectIndices: number[] = [];
    hops.forEach((hop, i) => {
      if (hop.type === 'object') objectIndices.push(i);
    });

    const hiddenCount = objectIndices.length - startCount - endCount;
    if (hiddenCount < 2) {
      return { startHops: hops, endHops: EMPTY_HOP_LIST, collapsedCount: 0 };
    }

    const startEndIdx = objectIndices[startCount - 1];
    const endStartIdx = objectIndices[objectIndices.length - endCount];

    return {
      startHops: hops.slice(0, startEndIdx + 1),
      endHops: hops.slice(endStartIdx),
      collapsedCount: hiddenCount,
    };
  }, [collapsible, expanded, hops]);

  const allVisibleHops = collapsedCount > 0 ? [...startHops, ...endHops] : hops;

  const lastStartHop = startHops[startHops.length - 1];

  const firstDisplayHop = startHops[0];
  const lastDisplayHop =
    endHops.length > 0 ? endHops[endHops.length - 1] : startHops[startHops.length - 1];
  const edgeObjectSpacerStart = firstDisplayHop?.type === 'object' ? firstDisplayHop : undefined;
  const edgeObjectSpacerEnd = lastDisplayHop?.type === 'object' ? lastDisplayHop : undefined;

  const css = merge(
    gutterCSS(gutter),
    widthCSS(width),
    maxWidthCSS(maxWidth),
    minWidthCSS(minWidth),
  );

  return (
    <HopsContainer direction={direction} css={css} ref={containerRef}>
      {accessLevel && (
        <HopsAccess>
          <FeatureBadge type={accessLevel} />
        </HopsAccess>
      )}
      <HopsDisplay direction={direction}>
        {edgeObjectSpacerStart && (
          <Hop
            key={`${edgeObjectSpacerStart.id}__edge-spacer-start`}
            direction={direction}
            hop={{ ...edgeObjectSpacerStart, visibility: 'hidden' }}
            previewHandle={previewHandle}
            size={size}
          />
        )}
        {startHops.map((hop, i) => {
          const augmented = augmentSpanHopForObjectNeighbors(hop, i, startHops);
          return (
            <Hop
              key={hop.id}
              direction={direction}
              hop={{
                ...augmented,
                ...(augmented.type === 'object' || augmented.type === 'span'
                  ? { visibility: 'visible' }
                  : {}),
              }}
              previewHandle={previewHandle}
              size={size}
            />
          );
        })}
        {collapsedCount > 0 && (
          <HopCollapseIndicator
            direction={direction}
            count={collapsedCount}
            onExpand={() => setExpanded(true)}
          />
        )}
        {endHops.map((hop, i) => {
          const augmented = augmentSpanHopForObjectNeighbors(hop, i, endHops, lastStartHop);
          return (
            <Hop
              key={hop.id}
              direction={direction}
              hop={{
                ...augmented,
                ...(augmented.type === 'object' || augmented.type === 'span'
                  ? { visibility: 'visible' }
                  : {}),
              }}
              previewHandle={previewHandle}
              size={size}
            />
          );
        })}
        {edgeObjectSpacerEnd && (
          <Hop
            key={`${edgeObjectSpacerEnd.id}__edge-spacer-end`}
            direction={direction}
            hop={{ ...edgeObjectSpacerEnd, visibility: 'hidden' }}
            previewHandle={previewHandle}
            size={size}
          />
        )}
      </HopsDisplay>
      <HopsSizer aria-hidden inert direction={direction}>
        {edgeObjectSpacerStart && (
          <Hop
            key={`${edgeObjectSpacerStart.id}__edge-spacer-start`}
            direction={direction}
            hop={{ ...edgeObjectSpacerStart, visibility: 'hidden' }}
            previewHandle={previewHandle}
            size={size}
          />
        )}
        {allVisibleHops
          .filter((h): h is ObjectOrSpanHop => h.type === 'object' || h.type === 'span')
          .map((hop, i) => {
            const augmented = augmentSpanHopForObjectNeighbors(hop, i, allVisibleHops);
            return (
              <Hop
                key={hop.id}
                direction={direction}
                hop={{ ...augmented, visibility: 'hidden' }}
                previewHandle={previewHandle}
                size={size}
              />
            );
          })}
        {edgeObjectSpacerEnd && (
          <Hop
            key={`${edgeObjectSpacerEnd.id}__edge-spacer-end`}
            direction={direction}
            hop={{ ...edgeObjectSpacerEnd, visibility: 'hidden' }}
            previewHandle={previewHandle}
            size={size}
          />
        )}
      </HopsSizer>
      <PreviewCard handle={previewHandle}>
        {(props: { payload: unknown }) =>
          props.payload != null ? (
            <HopsSharedPreviewContent payload={props.payload as HopsPreviewPayload} />
          ) : null
        }
      </PreviewCard>
    </HopsContainer>
  );
}
